/**
 * PalaceCell：单宫格（4×4 外圈十二宫之一）
 * 文墨天机风格：星曜竖列（名竖排+亮度下+四化徽标列下）；自化边界箭头（离心向外/向心向内）；
 * 右下人事宫竖列（自下而上：本命→大限→流年→流月→流日，两字缩写）+ 长生竖列 + 干支竖排；左下大限年龄段
 */
import type { ReactNode } from 'react';
import type { Palace, Zhi } from '../types/base';
import type { LayerPacks } from './layer-meta';
import { activeLayers, LAYER_CHAR, type LayerKey } from './layer-meta';
import {
  KIND_CHAR,
  SELF_ARROW_CLASS,
  SELF_ARROW_POS,
  SELF_IN_ARROW_POS,
  palaceSelfArrows,
  parseStarTokens,
  selfArrowChar,
  selfInArrowChar,
  starAnnotations,
  type StarToken,
} from './star-annot';

/** 星曜分类着色（图一：主星红、辅星蓝、杂曜灰黑） */
const CATEGORY_CLASS: Record<StarToken['category'], string> = {
  zhuxing: 'font-semibold text-red-700',
  fuxing: 'text-blue-700',
  zayao: 'text-slate-600',
};

/** 吉星（8 吉星）：紫色显示（覆盖辅星蓝色）；其余星曜颜色不变 */
const JI_STAR_SET = new Set(['文昌', '文曲', '左辅', '右弼', '天魁', '天钺', '禄存', '天马']);

/** 6 煞星：深蓝色显示（与吉星紫色拉开明显差距，避免蓝紫混淆） */
const SHA_STAR_SET = new Set(['擎羊', '陀罗', '火星', '铃星', '地空', '地劫']);

/** 字号 → Tailwind 完整 class 映射（JIT 扫源码取字面量，禁止动态拼接数值） */
const FONT_CLASS: Record<number, string> = {
  5: 'text-[5px]', 6: 'text-[6px]', 7: 'text-[7px]', 8: 'text-[8px]',
  9: 'text-[9px]', 10: 'text-[10px]', 11: 'text-[11px]', 12: 'text-[12px]',
  13: 'text-[13px]', 14: 'text-[14px]', 15: 'text-[15px]', 16: 'text-[16px]',
};
const FONT_CLASS_SM: Record<number, string> = {
  5: 'sm:text-[5px]', 6: 'sm:text-[6px]', 7: 'sm:text-[7px]', 8: 'sm:text-[8px]',
  9: 'sm:text-[9px]', 10: 'sm:text-[10px]', 11: 'sm:text-[11px]', 12: 'sm:text-[12px]',
  13: 'sm:text-[13px]', 14: 'sm:text-[14px]', 15: 'sm:text-[15px]', 16: 'sm:text-[16px]',
};

/** 四化底色（飞星落点星：禄绿/权紫/科蓝/忌红） */
const FLY_BG: Record<string, string> = {
  化禄: 'bg-green-500',
  化权: 'bg-purple-500',
  化科: 'bg-blue-500',
  化忌: 'bg-red-500',
};

/** 星名颜色：吉星紫 / 煞星深蓝 / 主星红 / 辅星蓝 / 杂曜灰 */
function starNameClass(name: string, category: StarToken['category']): string {
  if (JI_STAR_SET.has(name)) return 'font-semibold text-purple-700';
  if (SHA_STAR_SET.has(name)) return 'font-semibold text-blue-800';
  return CATEGORY_CLASS[category];
}

/** 长生十二星（引擎已独立到 stars.changsheng 字段，前端按 category='changsheng' 读取） */

interface Props {
  palace: Palace;
  layer: LayerKey;
  packs: LayerPacks;
  /** 本命身宫地支：命中该宫时，右下长生上方竖排红字标注“身宫”（不再显示于中宫） */
  shenZhi?: Zhi;
  /** 大限年龄段（如 83-92，底部居中显示；仅本命盘使用） */
  daxianAgeRange?: string;
  /** 大限10年分布：该宫年份+年龄（如 {year:2020, age:39}；仅大限盘使用） */
  daxianYear?: { year: number; age: number };
  /** 左下三颗星（自下而上：流年岁前诸星 → 流年将前诸星 → 生年博士十二星法，对齐文墨天机图一） */
  leftStars?: { suiqian?: string; jiangqian?: string; boshi?: string };
  /** 是否本命盘（仅本命盘显示每宫「流年/小限虚岁」） */
  isBenming?: boolean;
  /** 每地支宫流年虚岁（前 7 个） */
  liunianByZhi?: Record<Zhi, number[]>;
  /** 每地支宫小限虚岁（前 7 个） */
  xiaoxianByZhi?: Record<Zhi, number[]>;
  /** 点击宫位的飞星四化落点（星 + 落点宫地支）；无点击为 null */
  activeFly?: { kind: string; star: string; zhi: Zhi }[];
  /** 立太极标签（地支 → 「X之Y」）；太极宫自身与流月/日/时层为 undefined */
  taijiLabels?: Partial<Record<Zhi, string>>;
  /** 点击宫位回调 */
  onClickPalace?: (zhi: Zhi) => void;
  /** 附加信息条 */
  extra?: ReactNode;
}

export default function PalaceCell({ palace, layer, packs, shenZhi, daxianAgeRange, daxianYear, leftStars, isBenming, liunianByZhi, xiaoxianByZhi, activeFly, taijiLabels, onClickPalace, extra }: Props) {
  const { zhi, gan } = palace;
  /** 叠层人事宫（自上而下渲染；DOM 列底为本命，对齐图二自下而上顺序） */
  const acts = activeLayers(layer, packs);
  const rowsTopToBottom = [...acts].reverse();
  const allTokens = parseStarTokens(palace);
  // 长生十二神：引擎已拆成 stars.changsheng 独立字段（category='changsheng'），不需再从杂曜按名匹配
  const changShengTokens = allTokens.filter((t) => t.category === 'changsheng');
  const tokens = allTokens.filter((t) => t.category !== 'changsheng');
  const annots = starAnnotations(palace, layer, packs);

  /** 本宫自化箭头：离心（外围向外）/向心（内侧线指向对宫） */
  const selfArrows = palaceSelfArrows(palace);
  const outArrows = selfArrows.filter((a) => a.dir === 'out');
  const inArrows = selfArrows.filter((a) => a.dir === 'in');
  
  /** 星曜区字号预算（每宫独立 + 统一基线，双端同参）：
   *  可用宽按窄屏≈83px 最坏估算（桌面宫格更宽但字号与 App 保持一致，用户口径），超出由 overflow-hidden 兜底；
   *  基线 = 7 星档（全盘典型星数）：11px；
   *  拥挤宫优先保主星+辅星+煞星（category ≠ zayao）吃基线，剩余宽度给杂曜（下限 5px）；
   *  四化只出现在主/辅星上，徽标字号跟随主星 */
  const allocFont = (width: number, base: number, zaMin: number) => {
    const nZa = tokens.filter((t) => t.category === 'zayao').length;
    const nMain = tokens.length - nZa;
    if (tokens.length === 0) return { main: base, za: base };
    const main = nMain > 0 ? Math.min(base, Math.floor((width - nZa * zaMin) / nMain)) : base;
    const mainF = Math.max(zaMin, main);
    const za = nZa > 0 ? Math.min(base, Math.floor((width - nMain * mainF) / nZa)) : base;
    return { main: mainF, za: Math.max(zaMin, za) };
  };
  const fm = allocFont(83, 11, 5); // 窄屏
  /** 桌面与窄屏同参：双端字号统一（用户口径），保留 sm: class 结构便于日后恢复独立档位 */
  const fd = allocFont(83, 11, 5);

  // 宫格高度严格跟随 grid 行高（1fr 均分可用纵向空间）：不设 min-h 下限——
  // 低视口下行高小于内容时由星曜区 overflow-hidden 在本格内裁切，杜绝宫格向下溢出被后绘宫格白底跨格遮盖；
  // 根节点不裁切，保自化箭头压线半内半外
  // px-px 横向内边距（窄屏 9 星单行排布：9×9px + 0 间距 = 81px ≤ 可用 83px）
  return (
    <div
      className="relative flex h-full cursor-pointer flex-col bg-white px-px py-1"
      onClick={() => onClickPalace?.(zhi)}
    >
      {extra}

      {/* 星曜区：文墨天机竖列（名竖排 → 亮度 → 四化徽标列下），单行排满（不换行），让主星辅星杂曜都在第一行
       *  窄屏星名 8px + 列间距 0：多星也能挤进单行；超宽时 overflow-hidden 在本格内裁切 */}
      <div className="flex flex-1 flex-nowrap content-start items-start gap-x-0 overflow-hidden pb-1 pt-0.5">
        {tokens.length === 0 ? (
          <span className="text-slate-300">—</span>
        ) : (
          tokens.map((t, i) => {
            const a = annots.get(t.name);
            const hasBadge = Boolean(a?.bornKind) || (a?.layerTags.length ?? 0) > 0;
            // 每星按分组取字号：杂曜一组、主/辅/煞一组；亮度 = 星名减 3px（保底 5px）；徽标跟随主星字号
            const isZa = t.category === 'zayao';
            const starM = isZa ? fm.za : fm.main;
            const starD = isZa ? fd.za : fd.main;
            const brightM = Math.max(5, starM - 3);
            const brightD = Math.max(5, starD - 3);
            return (
              <div key={`${t.name}${i}`} className="flex flex-col items-center">
                {/* 点击宫四化落点：本宫该星命中 → 星名加底色方框（禄绿/权紫/科蓝/忌红）；
                    px-0 py-0 框即字盒；落点星字号 +1px（封顶 16）放大突出星名，框随字自动变大；
                    data-fly-land 供 GridBoard 量取飞星虚线终点 */}
                {(() => {
                  const flyTag = activeFly?.find((f) => f.zhi === zhi && f.star === t.name);
                  const flyM = flyTag ? Math.min(16, starM + 1) : starM;
                  const flyD = flyTag ? Math.min(16, starD + 1) : starD;
                  return (
                    <span
                      data-fly-land={flyTag ? `${zhi}:${flyTag.kind}` : undefined}
                      className={`flex flex-col items-center leading-tight ${starNameClass(t.name, t.category)} ${
                        // 落点框留白：字形在行盒内偏上（上贴死、下自带 descent 留白），只补上边 pt-[1.5px] + 左右 px-[1px]，
                        // 行高不变→高度代价最小；多字竖排框 pt 只作用首字上方
                        flyTag ? `${FLY_BG[flyTag.kind]} rounded-none px-[1px] pt-[1.5px] text-white` : ''
                      }`}
                    >
                      {[...t.name].map((ch, j) => (
                        <span key={j} className={`${FONT_CLASS[flyM]} leading-none ${FONT_CLASS_SM[flyD]}`}>
                          {ch}
                        </span>
                      ))}
                    </span>
                  );
                })()}
                {/* 亮度（leading-[1.15]：收紧行盒，缩小与下方生年徽标的间隙，省出的高度给徽标框留白） */}
                <span className={`${FONT_CLASS[brightM]} leading-[1.15] text-slate-500 ${FONT_CLASS_SM[brightD]}`}>{t.brightness || '\u00A0'}</span>
                {/* 四化徽标竖列（对齐文墨天机：本命红 + 层色）；mt 归 0，与亮度的间隙由徽标自身 pt 提供；
                    不用负间距：实测 macOS 端 CJK 回退字体墨迹下隙仅 0.04px，负间距会把 pb 的下留白盖光（字贴底） */}
                {hasBadge && (
                  <span className="flex flex-col items-center">
                    {/* 徽标字号降 1 档（10px）+ 对称 py-[1px]：实测两端字体墨迹分布相反（macOS 上隙1.3/下隙0.04，
                        Android 上隙0.3/下隙~1），11px 下任何不贴边组合都超 6 连徽列高预算（~73px），
                        10px+py1 列高 72px：Web 上2.2/下1.04，App 上1.3/下2，两端均不贴边 */}
                    {a?.bornKind && (
                      <span className={`bg-red-600 px-[1px] py-[1px] ${FONT_CLASS[Math.max(5, fm.main - 1)]} font-bold leading-none text-white ${FONT_CLASS_SM[Math.max(5, fd.main - 1)]}`}>
                        {KIND_CHAR[a.bornKind]}
                      </span>
                    )}
                    {a?.layerTags.map((tag, j) => (
                      <span
                        key={j}
                        className={`px-[1px] py-[1px] ${FONT_CLASS[Math.max(5, fm.main - 1)]} font-bold leading-none text-white ${FONT_CLASS_SM[Math.max(5, fd.main - 1)]} ${tag.layer.badgeClass}`}
                        title={`${tag.layer.label}·${tag.kind}`}
                      >
                        {KIND_CHAR[tag.kind]}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* 离心自化：外围线压线，箭头向外（字号 15px 加粗 500） */}
      {outArrows.length > 0 && (
        <span className={`absolute z-10 flex gap-0.5 ${SELF_ARROW_POS[zhi]}`}>
          {outArrows.map((s, i) => (
            <span
              key={i}
              className={`text-[15px] font-medium ${SELF_ARROW_CLASS[s.kind]}`}
              title={`${s.star} 离心自化${KIND_CHAR[s.kind]}`}
            >
              {selfArrowChar(zhi, 'out')}
            </span>
          ))}
        </span>
      )}

      {/* 向心自化：本宫内侧线，箭头指向对宫（字号 15px 加粗 500） */}
      {inArrows.length > 0 && (
        <span className={`absolute z-10 flex gap-0.5 ${SELF_IN_ARROW_POS[zhi]}`}>
          {inArrows.map((s, i) => (
            <span
              key={i}
              className={`text-[15px] font-medium ${SELF_ARROW_CLASS[s.kind]}`}
              title={`${s.star} 向心自化${KIND_CHAR[s.kind]}`}
            >
              {selfInArrowChar(zhi)}
            </span>
          ))}
        </span>
      )}

      {/* 左下竖排：立太极标签（橙，仅本命/大限/流年点击时）在上，大限年龄段（本命盘）
          或大限10年年份/岁数（大限盘）在下；同柱堆叠天然不重叠；age-corner 6px 小字 */}
      {(taijiLabels?.[zhi] || daxianAgeRange || daxianYear) && (
        <span className="age-corner absolute bottom-1 left-0.5 flex flex-col items-start gap-0.5">
          {taijiLabels?.[zhi] && (
            <span className="font-semibold text-orange-600">{taijiLabels[zhi]}</span>
          )}
          {daxianAgeRange && <span className="font-semibold text-slate-600">{daxianAgeRange}</span>}
          {daxianYear && (
            <span className="flex flex-col font-semibold text-slate-600">
              <span>{daxianYear.year}年</span>
              <span>{daxianYear.age}岁</span>
            </span>
          )}
        </span>
      )}
      {/* （仅本命盘）流年/小限虚岁：紧靠宫格左边线，位于十二长生/岁前/将前上方；6px 小字 */}
      {isBenming && liunianByZhi?.[zhi] && (
        <div className="absolute bottom-11 left-0 flex flex-col items-start text-left">
          <span className="text-[6px] leading-tight text-slate-500">流年 {liunianByZhi[zhi].join(',')}</span>
          <span className="text-[6px] leading-tight text-slate-500">
            小限 {xiaoxianByZhi?.[zhi]?.join(',')}
          </span>
        </div>
      )}
      {/* 右下：三颗星（自下而上：岁前 → 将前 → 博士；DOM 自上而下：博士 → 将前 → 岁前）+ 人事宫竖列（自下而上）+ 长生干支合列（长生在上、干支在下）
       *  right-1 尽量靠右，给左下大限年龄让位 */}
      <div className="absolute bottom-1 right-1 flex items-end gap-0.5">
        {/* 三颗星列（人事宫左侧） */}
        {(leftStars?.suiqian || leftStars?.jiangqian || leftStars?.boshi) && (
          <span className="flex flex-col items-start justify-end leading-tight">
            {leftStars.boshi && (
              <span className="text-[8px] text-slate-600 sm:text-[9px]" title="生年博士十二星法">{leftStars.boshi}</span>
            )}
            {leftStars.jiangqian && (
              <span className="text-[8px] text-slate-600 sm:text-[9px]" title="流年将前诸星">{leftStars.jiangqian}</span>
            )}
            {leftStars.suiqian && (
              <span className="text-[8px] text-slate-600 sm:text-[9px]" title="流年岁前诸星">{leftStars.suiqian}</span>
            )}
          </span>
        )}
        {/* 人事宫竖列 */}
        <span className="flex flex-col items-end leading-tight">
          {rowsTopToBottom.map((a) => {
            const rn = a.renshigong[zhi];
            if (!rn) return null;
            return (
              <span key={a.meta.key} className={`flex items-center gap-0.5 text-[8px] sm:text-[9px] ${a.meta.textClass}`}>
                {LAYER_CHAR[a.meta.key]}
                {/* 交友宫惯例用"友"（大友/年友/小友/月友/日友/时友），其余宫用名首字 */}
                {rn === '交友' ? '友' : rn.charAt(0)}
              </span>
            );
          })}
          <span className="flex items-center gap-0.5 text-[9px] font-bold text-red-700 sm:text-[10px]">
            {palace.renshigong}
          </span>
        </span>
        {/* 长生竖列（抽离星曜区，放在干支上方，对齐旧排盘）；身宫竖排红字标注在长生上方（对齐文墨天机） */}
        <span className="flex flex-col items-center leading-tight">
          {shenZhi === zhi && (
            <span className="mb-0.5 flex flex-col items-center rounded-[2px] border border-solid border-red-500 px-0.5 font-bold leading-tight text-red-600">
              <span className="text-[8px] sm:text-[9px]">身</span>
              <span className="text-[8px] sm:text-[9px]">宫</span>
            </span>
          )}
          {changShengTokens.map((ct) => (
            <span key={ct.name} className="mb-0.5 flex flex-col items-center sm:mb-1">
              {[...ct.name].map((ch, j) => (
                <span key={j} className="text-[8px] text-slate-600 sm:text-[9px]">{ch}</span>
              ))}
              {ct.brightness && (
                <span className="text-[7px] text-slate-400 sm:text-[8px]">{ct.brightness}</span>
              )}
            </span>
          ))}
          {/* 干支同规格：天干字体大小/颜色/粗细与地支一致；data-fly-gan 供 GridBoard 量取飞星虚线起点 */}
          <span data-fly-gan={zhi} className="text-[11px] font-bold text-slate-700">{gan}</span>
          <span className="text-[11px] font-bold text-slate-700">{zhi}</span>
        </span>
      </div>
    </div>
  );
}
